import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import PruTable from '../../../../../../common/components/PruTable/PruTable';
import { useHistory } from 'react-router-dom';
import { getDefaultDisplayDate } from '../../../../../../common/utils/time-utils';
import { CRITERIA_PATH, SIMULATION_TOOL_PATH } from 'src/app/modules/SimulationTool/constants';
import { CriteriaPaginateList } from 'src/app/modules/SimulationTool/types/criteria-setting';
import { deleteCriteriaItem } from 'src/app/modules/SimulationTool/network/criteriaCrud';
import { useDispatch } from 'react-redux';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';

type CriteriaSettingListProps = {
  isLoading: boolean;
  criteriaList?: CriteriaPaginateList;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onSort: (sortingState: { key: string; value?: string }) => void;
};

type DialogState = {
  open: boolean;
  id: string;
};

const CriteriaSettingList: FC<CriteriaSettingListProps> = ({
  isLoading,
  onRefresh,
  onChangePage,
  criteriaList,
  onSort,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
  });

  const onDelete = async (id: string) => {
    try {
      await deleteCriteriaItem(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Criteria item deleted successfully`,
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-kpi-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
      />
      <PruTable
        disableBulkSelect
        dataSource={criteriaList?.items}
        isLoading={isLoading}
        onRefresh={onRefresh}
        title={Translation('criteriaSetting.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('criteriaSetting.newCriteria'),
            onClick: () => history.push(`${SIMULATION_TOOL_PATH}${CRITERIA_PATH}/create`),
          },
        ]}
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Criteria',
            onClick: (row) => history.push(`${SIMULATION_TOOL_PATH}${CRITERIA_PATH}/edit/${row._id}`),
          },
          {
            title: Translation('section.common.operation.delete'),
            tooltipText: 'Delete Criteria',
            onClick: (row) => setDialogState({ open: true, id: row._id }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('criteriaSetting.name'),
            renderData: (row) => row.name,
          },
          {
            keyIndex: 'type',
            align: 'center',
            displayName: Translation('criteriaSetting.type'),
            renderData: (row) => row.type,
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('criteriaSetting.createdAt'),
            renderData: (row) => getDefaultDisplayDate(row.createdAt),
            sortable: true,
            onSort: (sort) => {
              onSort({ key: 'createDateSort', value: sort['createdAt'] });
            },
          },
          {
            keyIndex: 'createdBy',
            displayName: Translation('criteriaSetting.createdBy'),
            renderData: (row) => row.createdBy,
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('criteriaSetting.lastUpdatedAt'),
            renderData: (row) => getDefaultDisplayDate(row.updatedAt),
            sortable: true,
            onSort: (sort) => {
              onSort({
                key: 'lastUpdateDateSort',
                value: sort['updatedAt'],
              });
            },
          },
          {
            keyIndex: 'updatedBy',
            displayName: Translation('criteriaSetting.lastUpdatedBy'),
            renderData: (row) => row.updatedBy,
          },
        ]}
        totalPages={criteriaList?.totalPages}
        totalRecords={criteriaList?.totalRecords}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default CriteriaSettingList;
